<template>
  <modal
    :name="modalName"
    width="96%"
    height="auto"
    :max-width="1600"
    :adaptive="true"
    :scrollable="true"
    @opened="opened"
    @before-close="beforeClose"
  >
    <section
      ref="dialog"
      class="integration-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="integration-record-title"
      tabindex="-1"
      @keydown="keydown"
    >
      <AppModalHeader
        ref="header"
        design="new"
        title-id="integration-record-title"
        :eyebrow="operations ? 'Message operation' : 'Integration log'"
        :title="
          detail
            ? `${names[integration]} · ${operations ? humanize(detail.operation_type) : humanize(detail.method)}`
            : operations
            ? 'Message operation'
            : 'Integration log'
        "
        @close="$emit('close')"
      />
      <div class="integration-dialog__body">
        <div class="integration-record-heading">
          <span class="integration-record-id">{{ id }}</span>
          <CopyButton :value="id" />
          <Status v-if="detail" :operation="operations ? detail : null" :result="operations ? null : detail.result" />
          <AppButton design="new" theme="dark" size="small" :disabled="loading" @click.native="load">
            {{ loading ? 'Refreshing…' : 'Refresh' }}
          </AppButton>
        </div>
        <div v-if="error" class="integration-error" role="alert">
          {{ error }}
          <AppButton design="new" theme="dark" size="small" @click.native="load">Retry loading</AppButton>
        </div>
        <div v-if="loading && !detail" class="integration-skeleton" role="status" aria-label="Loading record">
          <span v-for="index in 5" :key="index" />
        </div>
        <template v-if="detail">
          <OperationDetail v-if="operations" :detail="detail" @refresh="load" @changed="changed" @denied="deny" />
          <LogDetail v-else :detail="detail" :integration="integration" />
        </template>
      </div>
    </section>
  </modal>
</template>

<script>
import { getMessageOperation, getIntegrationLog } from '@/http'
import CopyButton from './CopyButton'
import Status from './Status'
import OperationDetail from './OperationDetail'
import LogDetail from './LogDetail'
import { integrations, humanize, errorMessage } from './model'
export default {
  components: { CopyButton, Status, OperationDetail, LogDetail },
  props: { id: String, operations: Boolean, integration: String },
  data: () => ({ detail: null, loading: false, error: '', generation: 0, names: integrations, pollTimer: null }),
  computed: {
    modalName() { return `integration-record-${this.operations ? 'operation' : 'log'}-${this.integration}-${this.id}` }
  },
  created() { this.load() },
  mounted() {
    this.$modal.show(this.modalName)
    this.pollTimer = setInterval(() => {
      if (this.integration === 'knowledge_ai' && this.operations && !document.hidden && !this.loading && this.detail && ['pending', 'running', 'retry_wait', 'blocked'].includes(this.detail.execution_status)) this.load()
    }, 5000)
  },
  beforeDestroy() {
    this.generation++
    clearInterval(this.pollTimer)
    document.removeEventListener('focusin', this.keepFocus)
  },
  methods: {
    humanize,
    opened() {
      this.$refs.header.focusClose()
      document.addEventListener('focusin', this.keepFocus)
    },
    beforeClose(event) {
      event.cancel()
      this.$emit('close')
    },
    async load() {
      const generation = ++this.generation
      this.loading = true
      this.error = ''
      try {
        const response = await (this.operations ? getMessageOperation(this.id) : getIntegrationLog(this.integration, this.id))
        if (generation !== this.generation) return
        if (this.operations && response.data.integration !== this.integration && integrations[response.data.integration]) {
          const workspace = 'message-operations'
          const query = { [this.operations ? 'operation' : 'log']: this.id }
          await this.$router.replace({ name: `tech-support-${workspace}-${response.data.integration}`, query })
        } else this.detail = response.data
      } catch (error) {
        if (generation === this.generation) {
          this.error = errorMessage(error, this.operations ? 'Operation' : 'Log')
          this.detail = null
        }
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },
    changed() { this.load(); this.$emit('changed') },
    deny() { this.generation++; this.detail = null; this.loading = false; this.error = 'Access denied. You do not have permission to view this data.' },
    keepFocus(event) { if (!this.$refs.dialog.contains(event.target)) this.$refs.header.focusClose() },
    keydown(event) {
      if (event.key !== 'Tab') return
      const elements = Array.from(this.$refs.dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),[tabindex="0"]')).filter(element => element.getClientRects().length)
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
  }
}
</script>
